import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonOption } from '../../Fundamentals/Button';
import Drawer from '../../Fundamentals/Drawer/Drawer';
import EnumSelect from '../../Fundamentals/Form/EnumSelect';
import FormField from '../../Fundamentals/Form/FormField';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import SkynetIconButton from '../../Fundamentals/IconButton/SkynetIconButton';
import SegmentedControl from '../../Fundamentals/SegmentedControl/SegmentedControl';
import Spinner, { SpinnerPosition, SpinnerSize } from '../../Fundamentals/Spinner/Spinner';
import { ProductGroupTypeLabel } from './PackageTierCard';
import {
    allowsTierPricing,
    chargePlanTitle,
    findChargePlan,
    isOptionalPrice,
    isPrice,
    isValidChargePlanDraft,
    isValidProductPricing,
    isValidRepeatEvery,
    isValidStepLimit,
    toChargePlanDraft,
    withAddedStep,
} from './chargePlanUtils';
import { withSek } from '../../PriceSimulation/worksheetFormatters';
import { isOptionalWholeNumber, parseNumberInput, sortTiersByRank } from './tierUtils';
import { VossPricingScheme, VossPricingSchemeText } from '../../../Constants/VOSS/vossPricingScheme';
import { VossUseRevenueRecognitionText, revenueRecognitionModes } from '../../../Constants/VOSS/vossUseRevenueRecognition';
import type {
    ChargePlanDraft,
    GroupPricingDraft,
    PackageChargePlan,
    PackagePlan,
    PackageProduct,
    PackageProductGroup,
    PackageTier,
    PricingStepDraft,
    ProductPricingDraft,
} from './Packages.types';

const pricingSchemes: VossPricingScheme[] = [VossPricingScheme.UnitPricing, VossPricingScheme.TierPricing];

const wholeNumberError = (value: number | null, message: string): string | undefined => (isOptionalWholeNumber(value) ? undefined : message);

interface StepRowsProps {
    steps: PricingStepDraft[];
    onChange: (steps: PricingStepDraft[]) => void;
}

const StepRows = ({ steps, onChange }: StepRowsProps) => {
    const { t } = useTranslation();
    const update = (index: number, change: Partial<PricingStepDraft>) => onChange(steps.map((step, i) => (i === index ? { ...step, ...change } : step)));
    const hasInvalidLimit = steps.some((_, index) => !isValidStepLimit(steps, index));
    const hasMissingPrice = steps.some((step) => !isPrice(step.unitPrice) || !isOptionalPrice(step.flatPrice));
    const hasInvalidRepeat = steps.some((step) => !isValidRepeatEvery(step.repeatEvery));

    return (
        <div className="mb-8">
            <div className="d-flex gap-8 text-secondary font-13 mb-4">
                <span className="flex-1 min-w-0">{t('packages.upToQuantity')}</span>
                <span className="flex-1 min-w-0">{t('packages.pricePerUnit')}</span>
                <span className="flex-1 min-w-0">{t('packages.flatPrice')}</span>
                <span className="flex-1 min-w-0">{t('packages.repeatEvery')}</span>
                {/* keeps the headers above their inputs while the rows carry a delete button */}
                <span className="pl-32" />
            </div>
            {steps.map((step, index) => {
                const isLast = index === steps.length - 1;
                const label = (field: string) => t('packages.stepField', { field, step: index + 1 });

                return (
                    <div key={index} className="d-flex align-items-center gap-8 mb-8">
                        <span className="flex-1 min-w-0">
                            {isLast ? (
                                <span className="text-secondary">{t('packages.unlimited')}</span>
                            ) : (
                                <input
                                    type="number"
                                    min={0}
                                    step={1}
                                    className="form-control"
                                    aria-label={label(t('packages.upToQuantity'))}
                                    aria-invalid={!isValidStepLimit(steps, index)}
                                    value={step.upperLimit ?? ''}
                                    onChange={(e) => update(index, { upperLimit: parseNumberInput(e.target.value) })}
                                />
                            )}
                        </span>
                        <span className="flex-1 min-w-0">
                            <input
                                type="number"
                                min={0}
                                step={0.01}
                                className="form-control"
                                aria-label={label(t('packages.pricePerUnit'))}
                                value={step.unitPrice ?? ''}
                                onChange={(e) => update(index, { unitPrice: parseNumberInput(e.target.value) })}
                            />
                        </span>
                        <span className="flex-1 min-w-0">
                            <input
                                type="number"
                                min={0}
                                step={0.01}
                                className="form-control"
                                aria-label={label(t('packages.flatPrice'))}
                                value={step.flatPrice ?? ''}
                                onChange={(e) => update(index, { flatPrice: parseNumberInput(e.target.value) })}
                            />
                        </span>
                        <span className="flex-1 min-w-0">
                            <input
                                type="number"
                                min={1}
                                step={1}
                                className="form-control"
                                aria-label={label(t('packages.repeatEvery'))}
                                aria-invalid={!isValidRepeatEvery(step.repeatEvery)}
                                value={step.repeatEvery ?? ''}
                                onChange={(e) => update(index, { repeatEvery: parseNumberInput(e.target.value) })}
                            />
                        </span>
                        {isLast ? (
                            <span className="pl-32" />
                        ) : (
                            <SkynetIconButton icon="delete" label={t('packages.removeStep', { step: index + 1 })} onClick={() => onChange(steps.filter((_, i) => i !== index))} />
                        )}
                    </div>
                );
            })}
            {hasInvalidLimit && <span className="d-block text-error font-13 mb-8">{t('packages.stepLimitInvalid')}</span>}
            {hasMissingPrice && <span className="d-block text-error font-13 mb-8">{t('packages.stepPriceInvalid')}</span>}
            {hasInvalidRepeat && <span className="d-block text-error font-13 mb-8">{t('packages.stepRepeatInvalid')}</span>}
            <button type="button" className="btn btn-tertiary btn-sm" onClick={() => onChange(withAddedStep(steps))}>
                <SpirisIcon name="plus" size="sm" className="mr-8" />
                {t('packages.addStep')}
            </button>
        </div>
    );
};

interface ProductPricingFieldsProps {
    idPrefix: string;
    product: ProductPricingDraft;
    info: PackageProduct | undefined;
    onChange: (product: ProductPricingDraft) => void;
}

const ProductPricingFields = ({ idPrefix, product, info, onChange }: ProductPricingFieldsProps) => {
    const { t } = useTranslation();
    const contentId = useId();
    const isValid = isValidProductPricing(product);
    // starts folded so a long tier stays short; the folded header flags whatever is still incomplete
    const [isOpen, setIsOpen] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const update = <K extends keyof ProductPricingDraft>(key: K, value: ProductPricingDraft[K]) => onChange({ ...product, [key]: value });
    const isTiered = product.pricingScheme === VossPricingScheme.TierPricing;
    const wholeNumber = t('packages.offsetInvalid') ?? '';
    const summary = isTiered ? t('packages.stepCount', { count: product.steps.length }) : withSek(product.unitPrice ?? 0);

    return (
        <div className="border rounded-8 p-12 mb-8">
            <div className="d-flex align-items-start justify-content-between flex-wrap gap-12">
                <button
                    type="button"
                    className="bg-transparent border-0 p-0 cursorpointer d-flex align-items-start gap-8 text-left"
                    aria-expanded={isOpen}
                    aria-controls={contentId}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <SpirisIcon name={isOpen ? 'chevron-down' : 'chevron-right'} size="sm" className="icon-neutral mt-4" />
                    <span>
                        <span className="d-block font-weight-semibold">{info?.name}</span>
                        <code className="text-secondary font-weight-normal font-size-xs">{info?.articleNumber}</code>
                        {!isOpen && <span className={`d-block font-13 mt-4 ${isValid ? 'text-secondary' : 'text-error'}`}>{isValid ? summary : t('packages.incomplete')}</span>}
                    </span>
                </button>
                {/* a Main or Addon already on tier steps (Draft lets VOSS save it) keeps the toggle so it can move back to a unit price */}
                {(allowsTierPricing(info?.type) || isTiered) && (
                    <SegmentedControl<VossPricingScheme>
                        size="sm"
                        className="mb-0"
                        ariaLabel={t('packages.pricingModelFor', { name: info?.name ?? '' })}
                        value={product.pricingScheme}
                        onChange={(scheme) => update('pricingScheme', scheme)}
                        options={pricingSchemes.map((scheme) => ({ value: scheme, label: t(VossPricingSchemeText[scheme]) }))}
                    />
                )}
            </div>
            {isOpen && (
                <div id={contentId} className="mt-8">
                    {isTiered ? (
                        <StepRows steps={product.steps} onChange={(steps) => update('steps', steps)} />
                    ) : (
                        <FormField
                            label={t('packages.unitPrice') ?? ''}
                            htmlFor={`${idPrefix}-unit-price`}
                            required
                            error={isPrice(product.unitPrice) ? undefined : (t('packages.priceInvalid') ?? '')}
                        >
                            <input
                                id={`${idPrefix}-unit-price`}
                                type="number"
                                min={0}
                                step={0.01}
                                className="form-control"
                                value={product.unitPrice ?? ''}
                                onChange={(e) => update('unitPrice', parseNumberInput(e.target.value))}
                            />
                        </FormField>
                    )}
                    <div className="d-flex flex-wrap gap-16">
                        <FormField
                            label={t('packages.includedQuantity') ?? ''}
                            htmlFor={`${idPrefix}-included`}
                            inline
                            error={wholeNumberError(product.includedQuantity, wholeNumber)}
                        >
                            <input
                                id={`${idPrefix}-included`}
                                type="number"
                                min={0}
                                step={1}
                                className="form-control"
                                value={product.includedQuantity ?? ''}
                                onChange={(e) => update('includedQuantity', parseNumberInput(e.target.value))}
                            />
                        </FormField>
                        <FormField
                            label={t('packages.maxQuantity') ?? ''}
                            htmlFor={`${idPrefix}-max`}
                            inline
                            help={t('packages.maxQuantityHelp') ?? ''}
                            error={wholeNumberError(product.maxQuantity, wholeNumber)}
                        >
                            <input
                                id={`${idPrefix}-max`}
                                type="number"
                                min={0}
                                step={1}
                                className="form-control"
                                value={product.maxQuantity ?? ''}
                                onChange={(e) => update('maxQuantity', parseNumberInput(e.target.value))}
                            />
                        </FormField>
                    </div>
                    <button type="button" className="btn btn-link px-0 py-4" aria-expanded={showAdvanced} onClick={() => setShowAdvanced(!showAdvanced)}>
                        <SpirisIcon name={showAdvanced ? 'chevron-down' : 'chevron-right'} size="sm" className="mr-8" />
                        {t('packages.advanced')}
                    </button>
                    {showAdvanced && (
                        <div className="d-flex flex-wrap gap-16 mt-8">
                            <FormField
                                label={t('packages.articleNumberOverride') ?? ''}
                                htmlFor={`${idPrefix}-article`}
                                inline
                                help={t('packages.articleNumberOverrideHelp') ?? ''}
                            >
                                <input
                                    id={`${idPrefix}-article`}
                                    type="text"
                                    className="form-control"
                                    value={product.articleNumber}
                                    onChange={(e) => update('articleNumber', e.target.value)}
                                />
                            </FormField>
                            <FormField label={t('packages.tierPricingGroup') ?? ''} htmlFor={`${idPrefix}-tier-group`} inline help={t('packages.tierPricingGroupHelp') ?? ''}>
                                <input
                                    id={`${idPrefix}-tier-group`}
                                    type="text"
                                    className="form-control"
                                    value={product.tierPricingGroup}
                                    onChange={(e) => update('tierPricingGroup', e.target.value)}
                                />
                            </FormField>
                            <FormField
                                label={t('packages.rank') ?? ''}
                                htmlFor={`${idPrefix}-rank`}
                                inline
                                help={t('packages.pricingRankHelp') ?? ''}
                                error={wholeNumberError(product.rank, wholeNumber)}
                            >
                                <input
                                    id={`${idPrefix}-rank`}
                                    type="number"
                                    min={0}
                                    step={1}
                                    className="form-control"
                                    value={product.rank ?? ''}
                                    onChange={(e) => update('rank', parseNumberInput(e.target.value))}
                                />
                            </FormField>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

interface GroupPricingFieldsProps {
    group: PackageProductGroup;
    pricing: GroupPricingDraft;
    onChange: (pricing: GroupPricingDraft) => void;
}

const GroupPricingFields = ({ group, pricing, onChange }: GroupPricingFieldsProps) => {
    const { t } = useTranslation();
    const idPrefix = `charge-plan-${pricing.productGroupId}`;
    const products = new Map((group.products ?? []).map((product) => [product.id, product]));
    const update = <K extends keyof GroupPricingDraft>(key: K, value: GroupPricingDraft[K]) => onChange({ ...pricing, [key]: value });

    return (
        <div className="border rounded-8 p-16 mb-16">
            <div className="d-flex align-items-center flex-wrap gap-8 mb-12">
                <SpirisIcon name="purchasing" size="sm" className="icon-neutral" />
                <span className="font-weight-semibold">{group.name}</span>
                {group.productGroupType !== undefined && <ProductGroupTypeLabel type={group.productGroupType} />}
            </div>
            <div className="d-flex flex-wrap gap-16">
                <FormField
                    label={t('packages.articleNumber') ?? ''}
                    htmlFor={`${idPrefix}-article`}
                    required
                    inline
                    help={t('packages.groupArticleNumberHelp') ?? ''}
                    error={pricing.articleNumber.trim().length === 0 ? (t('packages.articleNumberRequired') ?? '') : undefined}
                >
                    <input
                        id={`${idPrefix}-article`}
                        type="text"
                        className="form-control"
                        value={pricing.articleNumber}
                        onChange={(e) => update('articleNumber', e.target.value)}
                    />
                </FormField>
                <FormField label={t('packages.invoiceGroup') ?? ''} htmlFor={`${idPrefix}-invoice-group`} inline help={t('packages.invoiceGroupHelp') ?? ''}>
                    <input
                        id={`${idPrefix}-invoice-group`}
                        type="text"
                        className="form-control"
                        value={pricing.invoiceGroup}
                        onChange={(e) => update('invoiceGroup', e.target.value)}
                    />
                </FormField>
                <FormField label={t('packages.useRevenueRecognition') ?? ''} htmlFor={`${idPrefix}-revenue`} inline>
                    <EnumSelect
                        id={`${idPrefix}-revenue`}
                        value={pricing.useRevenueRecognition}
                        options={revenueRecognitionModes}
                        getLabel={(mode) => t(VossUseRevenueRecognitionText[mode])}
                        onChange={(mode) => update('useRevenueRecognition', mode)}
                    />
                </FormField>
            </div>
            <h5 className="font-size-xs text-uppercase text-secondary mb-8">{t('packages.products')}</h5>
            {pricing.products.map((product, index) => (
                <ProductPricingFields
                    key={product.productId}
                    idPrefix={`${idPrefix}-${product.productId}`}
                    product={product}
                    info={products.get(product.productId)}
                    onChange={(changed) =>
                        update(
                            'products',
                            pricing.products.map((current, i) => (i === index ? changed : current))
                        )
                    }
                />
            ))}
        </div>
    );
};

interface PackageChargePlanEditorDrawerProps {
    chargePlan?: PackageChargePlan;
    initialTierId?: string;
    initialPlanId?: string;
    tiers: PackageTier[];
    plans: PackagePlan[];
    chargePlans: PackageChargePlan[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (draft: ChargePlanDraft) => void;
    onDelete?: () => void;
}

const PackageChargePlanEditorDrawer = ({
    chargePlan,
    initialTierId,
    initialPlanId,
    tiers,
    plans,
    chargePlans,
    isSaving,
    onClose,
    onSave,
    onDelete,
}: PackageChargePlanEditorDrawerProps) => {
    const { t } = useTranslation();
    const isNew = chargePlan === undefined;
    const findTier = (tierId: string | undefined) => tiers.find((tier) => tier.id === tierId);
    const [draft, setDraft] = useState<ChargePlanDraft>(() =>
        toChargePlanDraft(findTier(chargePlan?.tierId ?? initialTierId), chargePlan?.planId ?? initialPlanId ?? '', chargePlan)
    );

    const tier = findTier(draft.tierId);
    const plan = plans.find((candidate) => candidate.id === draft.planId);
    const groups = new Map((tier?.productGroups ?? []).map((group) => [group.id, group]));
    // a tier and plan pair can only have one charge plan
    const isTaken = (planId: string | undefined) => isNew && findChargePlan(chargePlans, draft.tierId, planId) !== undefined;
    const canSave = isValidChargePlanDraft(draft) && !isTaken(draft.planId) && !isSaving;

    const updateGroup = (changed: GroupPricingDraft) =>
        setDraft((prev) => ({ ...prev, groups: prev.groups.map((current) => (current.productGroupId === changed.productGroupId ? changed : current)) }));

    return (
        <Drawer
            title={(isNew ? t('packages.newChargePlan') : t('packages.editChargePlan')) ?? ''}
            subtitle={isNew ? (t('packages.chargePlanEditorSubtitle') ?? '') : chargePlanTitle(tier, plan)}
            onClose={onClose}
            canClose={!isSaving}
            footer={
                <>
                    {!isNew && onDelete !== undefined && (
                        <Button option={ButtonOption.DESTRUCTIVE} className="mr-auto" disabled={isSaving} onClick={onDelete}>
                            {t('packages.deleteChargePlan')}
                        </Button>
                    )}
                    {isSaving && <Spinner size={SpinnerSize.SMALL} position={SpinnerPosition.LEFT} />}
                    <Button disabled={isSaving} onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button option={ButtonOption.PRIMARY} disabled={!canSave} onClick={() => onSave(draft)}>
                        {isNew ? t('packages.createChargePlan') : t('common.save')}
                    </Button>
                </>
            }
        >
            <div className="d-flex flex-wrap gap-16">
                <FormField label={t('packages.tier') ?? ''} htmlFor="charge-plan-tier" required inline help={isNew ? undefined : (t('packages.lockedWhenEditing') ?? '')}>
                    <select
                        id="charge-plan-tier"
                        className="form-control dropdown-field"
                        value={draft.tierId}
                        disabled={!isNew}
                        onChange={(e) => setDraft((prev) => ({ ...toChargePlanDraft(findTier(e.target.value), prev.planId), invoicingId: prev.invoicingId }))}
                    >
                        <option value="">{t('packages.selectTier')}</option>
                        {sortTiersByRank(tiers).map((option) => (
                            <option key={option.id} value={option.id}>
                                {option.name}
                            </option>
                        ))}
                    </select>
                </FormField>
                <FormField
                    label={t('packages.plan') ?? ''}
                    htmlFor="charge-plan-plan"
                    required
                    inline
                    help={isNew ? undefined : (t('packages.lockedWhenEditing') ?? '')}
                    error={isTaken(draft.planId) ? (t('packages.chargePlanTaken') ?? '') : undefined}
                >
                    <select
                        id="charge-plan-plan"
                        className="form-control dropdown-field"
                        value={draft.planId}
                        disabled={!isNew}
                        onChange={(e) => setDraft((prev) => ({ ...prev, planId: e.target.value }))}
                    >
                        <option value="">{t('packages.selectPlan')}</option>
                        {plans.map((option) => (
                            <option key={option.id} value={option.id} disabled={isTaken(option.id)}>
                                {option.name}
                            </option>
                        ))}
                    </select>
                </FormField>
                <FormField label={t('packages.invoicingId') ?? ''} htmlFor="charge-plan-invoicing-id" inline help={t('packages.invoicingIdHelp') ?? ''}>
                    <input
                        id="charge-plan-invoicing-id"
                        type="text"
                        className="form-control"
                        value={draft.invoicingId}
                        onChange={(e) => setDraft((prev) => ({ ...prev, invoicingId: e.target.value }))}
                    />
                </FormField>
            </div>

            {tier !== undefined && (
                <>
                    <h4 className="mt-16 font-16 mb-4">{t('packages.groupPricingTitle')}</h4>
                    <p className="text-secondary font-13 mb-8">{t(draft.groups.length > 0 ? 'packages.allGroupsPriced' : 'packages.noGroupsToPrice')}</p>
                    {draft.groups.map((pricing) => {
                        const group = groups.get(pricing.productGroupId);
                        return group === undefined ? null : <GroupPricingFields key={pricing.productGroupId} group={group} pricing={pricing} onChange={updateGroup} />;
                    })}
                </>
            )}
        </Drawer>
    );
};

export default PackageChargePlanEditorDrawer;
